/**
 * AtlasGroupsModule (P9) — the Groups, Charters, Treasuries & Forks UI.
 *
 * This is the player-facing face of System Gamma's organizational layer. It
 * RENDERS the replicated group rows wired into `atlas` state by
 * AtlasNetworkModule and turns every P9 mechanic into a button:
 *
 *   • Found a group — burn the GROUP_FORMATION_SOV fee, attach a P4 group-kind
 *     Hall, and author a public Charter that opens a 24h review window.
 *   • Sign / challenge a charter in review — ratify (needs Trustworthiness ≥
 *     GROUP_MIN_TRUST_TO_JOIN) or file a public objection that blocks activation.
 *   • Treasury — donate SOV (ledger-mirrored), and as an officer propose a
 *     disbursement that auto-executes when ≤10%/officer/UTC-day or opens a 50%
 *     member vote when larger; members vote, the proposing officer executes.
 *   • Embezzlement — any member reports an officer's theft, cleaving their
 *     Trustworthiness in half and demoting them to Member.
 *   • Fork / schism — a faction of ≥33% initiates a fork; members join or
 *     decline; the initiator resolves it, walking away with a proportional
 *     Treasury share (and the Hall only when the fork strictly exceeds 66%).
 *
 * The client has NO authority over any of this — every consequential value is
 * server-computed (the formation burn, the Treasury mirror, the vote tally, the
 * Trustworthiness penalty, the proportional split). On a SenderError the reducer
 * promise rejects and we surface `err.message`. The threshold helpers below
 * only PREVIEW the server math in `group_governance.ts`; the server decides.
 */

import type { App, IModule } from 'enginex';
import { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import type { CharterDoc, GroupMember, TreasurySpend, ForkVote } from '@shared/types/groups.js';
import {
  GROUP_FORMATION_SOV,
  GROUP_CHARTER_SIGNATURES_REQUIRED,
  GROUP_MIN_TRUST_TO_JOIN,
  GROUP_OFFICER_ROLES,
} from '@shared/constants.js';

/** Truncate a 64-char identity hex to a glanceable 8-char + ellipsis token. */
function shortHex(hex: string): string {
  return hex.length > 8 ? `${hex.slice(0, 8)}\u2026` : hex;
}

/** Wall-clock now in micros-since-epoch (server timestamps are micros). */
function nowMicros(): bigint {
  return BigInt(Date.now()) * 1000n;
}

/** Parse a positive whole SOV amount, or null if blank/invalid/non-positive. */
function parseAmount(raw: string): bigint | null {
  const t = raw.trim();
  if (!/^\d+$/.test(t)) return null;
  const v = BigInt(t);
  return v > 0n ? v : null;
}

/** Format a micros-since-epoch instant as a local human string. */
function fmtWhen(micros: bigint): string {
  if (micros <= 0n) return '—';
  return new Date(Number(micros / 1000n)).toLocaleString();
}

/** Escape the four HTML-significant characters before interpolating into innerHTML. */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** True for the officer subset {leader, treasurer} — the only spenders. */
function isOfficer(role: string): boolean {
  return GROUP_OFFICER_ROLES.includes(role);
}

// ── Preview-only mirrors of server group_governance math (server is authority) ──
/** Free daily disbursement ceiling: 10% of the current Treasury. */
function freeCap(treasury: bigint): bigint {
  return treasury / 10n;
}
/** YES votes needed for a vote-gated spend (50% of members, rounded up). */
function votePassNeeded(members: number): number {
  return Math.ceil(members / 2);
}
/** Faction size needed to INITIATE a fork (33% of members, rounded up). */
function forkInitNeeded(members: number): number {
  return Math.ceil((members * 3300) / 10000);
}
/** Faction size needed to take the Hall (strictly &gt; 66% of members). */
function forkHallNeeded(members: number): number {
  return Math.floor((members * 6600) / 10000) + 1;
}
/** Proportional Treasury share a leaving faction of `size` walks away with. */
function proportionalShare(treasury: bigint, size: number, members: number): bigint {
  if (members <= 0 || size >= members) return treasury;
  return (treasury * BigInt(size)) / BigInt(members);
}

/** Human label for a state badge. */
const STATE_LABEL: Record<string, string> = {
  in_review: 'In review',
  active: 'Active',
  dissolved: 'Dissolved',
  forked: 'Forked',
};
/** Badge accent class per state (reuses the events-tab badge palette). */
const STATE_CLS: Record<string, string> = {
  in_review: 'ritual',
  active: 'inst',
  dissolved: '',
  forked: '',
};

/** Sort priority — actionable states float to the top of the list. */
const STATE_ORDER: Record<string, number> = {
  in_review: 0,
  active: 1,
  forked: 2,
  dissolved: 3,
};

/**
 * Resolve a friendly label for an identity hex: "you" for self, the account
 * display name / username when known, else the short hex. Always pass the
 * result through `esc()` before interpolating into innerHTML.
 */
function label(hex: string): string {
  if (hex === atlas.identityHex) return 'you';
  const conn = atlas.conn;
  if (conn) {
    try {
      const acct = conn.db.account.identity.find(Identity.fromString(hex));
      if (acct) return acct.displayName || acct.username;
    } catch {
      /* malformed hex — fall through to short form */
    }
  }
  return shortHex(hex);
}

/** Status pill text + accent class for a spend proposal. */
function spendStatus(s: TreasurySpend): { text: string; cls: string } {
  if (s.executed) return { text: 'Executed', cls: 'inst' };
  if (s.approved) return { text: 'Approved — awaiting execution', cls: 'ok' };
  if (s.requiresVote) return { text: 'Vote open', cls: 'ritual' };
  return { text: 'Pending', cls: '' };
}

export class AtlasGroupsModule implements IModule {
  readonly name = 'AtlasGroups';

  private nameIn!: HTMLInputElement;
  private missionIn!: HTMLInputElement;
  private hallSelect!: HTMLSelectElement;
  private hallNewBtn!: HTMLButtonElement;
  private foundBtn!: HTMLButtonElement;
  private foundNote!: HTMLElement;
  private listEl!: HTMLElement;

  /** Name of a Hall just spawned, so refreshHalls can auto-select it once it replicates. */
  private pendingHall: string | null = null;

  init(_app: App): void {
    this.nameIn = document.getElementById('grp-name') as HTMLInputElement;
    this.missionIn = document.getElementById('grp-mission') as HTMLInputElement;
    this.hallSelect = document.getElementById('grp-hall') as HTMLSelectElement;
    this.hallNewBtn = document.getElementById('grp-hall-new') as HTMLButtonElement;
    this.foundBtn = document.getElementById('grp-found-btn') as HTMLButtonElement;
    this.foundNote = document.getElementById('grp-found-note') as HTMLElement;
    this.listEl = document.getElementById('grp-list') as HTMLElement;

    if (!this.listEl) return; // passport DOM absent (headless/test) — nothing to wire.

    this.foundBtn.addEventListener('click', () => this.found());
    this.hallNewBtn.addEventListener('click', () => this.createHall());
    // The list is rebuilt on every data change, so use event delegation.
    this.listEl.addEventListener('click', (e) => this.onListClick(e));

    // Group/treasury/vote/fork rows changed → rebuild the list (and the Hall picker).
    bus.on('atlas:groups-updated', () => {
      this.renderList();
      this.refreshHalls();
    });
    // A signature gate depends on the local Trustworthiness score.
    bus.on('atlas:reputation-updated', () => this.renderList());
    // Spawning a Hall burns SOV; the wallet tick is our cue the world replicated.
    bus.on('atlas:wallet-updated', () => this.refreshHalls());

    this.refreshHalls();
    this.renderList();
  }

  // ───────────────────────────── found a group ─────────────────────────────

  /** Group-kind worlds the local player owns that are not yet a group's Hall. */
  private myFreeHalls(): { worldId: bigint; name: string }[] {
    const conn = atlas.conn;
    const self = conn?.identity ?? null;
    if (!conn || !self) return [];
    const taken = new Set<string>();
    for (const g of atlas.groups.values()) {
      if (g.hallWorldId > 0n) taken.add(g.hallWorldId.toString());
    }
    const out: { worldId: bigint; name: string }[] = [];
    for (const w of conn.db.world.iter()) {
      if (w.kind !== 'group') continue;
      if (!w.owner.isEqual(self)) continue;
      if (taken.has(w.worldId.toString())) continue;
      out.push({ worldId: w.worldId, name: w.name });
    }
    out.sort((a, b) => (a.worldId < b.worldId ? -1 : a.worldId > b.worldId ? 1 : 0));
    return out;
  }

  /** Rebuild the Hall `<select>`, preserving the live selection (or a pending spawn). */
  private refreshHalls(): void {
    if (!this.hallSelect) return;
    const prev = this.hallSelect.value;
    const halls = this.myFreeHalls();
    this.hallSelect.innerHTML = '';
    if (halls.length === 0) {
      const o = document.createElement('option');
      o.value = '';
      o.textContent = '— no free Hall (create one) —';
      this.hallSelect.appendChild(o);
    } else {
      for (const h of halls) {
        const o = document.createElement('option');
        o.value = h.worldId.toString();
        o.textContent = `${h.name} (#${h.worldId})`;
        this.hallSelect.appendChild(o);
      }
    }
    if (this.pendingHall) {
      const match = halls.find((h) => h.name === this.pendingHall);
      if (match) {
        this.hallSelect.value = match.worldId.toString();
        this.pendingHall = null;
        return;
      }
    }
    if (prev && halls.some((h) => h.worldId.toString() === prev)) this.hallSelect.value = prev;
  }

  /** Spawn a fresh group-kind Hall world so a founder always has one to attach. */
  private createHall(): void {
    const conn = atlas.conn;
    if (!conn) {
      this.setNote('Not connected.', 'err');
      return;
    }
    const base = this.nameIn.value.trim() || 'Group';
    const hallName = `${base} Hall ${Math.floor(Math.random() * 9000 + 1000)}`;
    this.pendingHall = hallName;
    this.setNote(`Spawning Hall \u201c${hallName}\u201d\u2026`, '');
    this.hallNewBtn.disabled = true;
    void conn.reducers
      .spawnWorld({
        name: hallName,
        sizeM: 64,
        kind: 'group',
        lifecycle: 'persistent',
        governanceTemplate: 'solo',
        capacity: 10,
      })
      .then(() => {
        this.setNote(`Hall \u201c${hallName}\u201d ready \u2014 select it and found your group.`, 'ok');
        this.refreshHalls();
      })
      .catch((err: Error) => {
        this.pendingHall = null;
        this.setNote(err.message, 'err');
      })
      .finally(() => {
        this.hallNewBtn.disabled = false;
      });
  }

  /** Burn the formation fee, attach the chosen Hall, and open the charter for review. */
  private found(): void {
    const conn = atlas.conn;
    if (!conn) {
      this.setNote('Not connected.', 'err');
      return;
    }
    const name = this.nameIn.value.trim();
    if (name.length === 0) {
      this.setNote('Give your group a name.', 'err');
      return;
    }
    const hallRaw = this.hallSelect.value;
    if (!hallRaw) {
      this.setNote('Select or create a Hall world first.', 'err');
      return;
    }
    if (atlas.walletSov < GROUP_FORMATION_SOV) {
      this.setNote(
        `Founding costs ${GROUP_FORMATION_SOV} SOV \u2014 your balance is ${atlas.walletSov}.`,
        'err',
      );
      return;
    }
    const mission = this.missionIn.value.trim();
    const charter: CharterDoc = { mission, rules: [], tags: [] };
    const charterRef = JSON.stringify(charter);
    const hallWorldId = BigInt(hallRaw);
    this.setNote('Founding\u2026', '');
    this.foundBtn.disabled = true;
    void conn.reducers
      .proposeGroup({ name, charterRef, hallWorldId })
      .then(() => {
        this.setNote(`Founded \u2014 \u201c${name}\u201d is in public review.`, 'ok');
        this.nameIn.value = '';
        this.missionIn.value = '';
      })
      .catch((err: Error) => this.setNote(err.message, 'err'))
      .finally(() => {
        this.foundBtn.disabled = false;
      });
  }

  // ───────────────────────────── lookups ─────────────────────────────

  private membersOf(groupId: bigint): GroupMember[] {
    const out: GroupMember[] = [];
    for (const m of atlas.groupMembers.values()) if (m.groupId === groupId) out.push(m);
    return out;
  }

  private myMembership(groupId: bigint): GroupMember | null {
    const me = atlas.identityHex;
    if (!me) return null;
    for (const m of atlas.groupMembers.values()) {
      if (m.groupId === groupId && m.identity === me) return m;
    }
    return null;
  }

  private signatureCount(groupId: bigint): number {
    let n = 0;
    for (const s of atlas.groupSignatures.values()) if (s.groupId === groupId) n++;
    return n;
  }

  private haveISigned(groupId: bigint): boolean {
    const me = atlas.identityHex;
    if (!me) return false;
    for (const s of atlas.groupSignatures.values()) {
      if (s.groupId === groupId && s.signer === me) return true;
    }
    return false;
  }

  private challengesOf(groupId: bigint): { challenger: string; reason: string }[] {
    const out: { challenger: string; reason: string }[] = [];
    for (const c of atlas.charterChallenges.values()) {
      if (c.groupId === groupId) out.push({ challenger: c.challenger, reason: c.reason });
    }
    return out;
  }

  private spendsOf(groupId: bigint): TreasurySpend[] {
    const out: TreasurySpend[] = [];
    for (const s of atlas.treasurySpends.values()) if (s.groupId === groupId) out.push(s);
    out.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
    return out;
  }

  private spendTally(spendId: bigint): { yes: number; no: number } {
    let yes = 0;
    let no = 0;
    for (const v of atlas.spendVotes.values()) {
      if (v.spendId !== spendId) continue;
      if (v.yes) yes++;
      else no++;
    }
    return { yes, no };
  }

  private forksOf(groupId: bigint): ForkVote[] {
    const out: ForkVote[] = [];
    for (const f of atlas.forkVotes.values()) if (f.groupId === groupId) out.push(f);
    out.sort((a, b) => (a.openedAt < b.openedAt ? 1 : a.openedAt > b.openedAt ? -1 : 0));
    return out;
  }

  /** Effective faction size for an open fork: the initiating list ∪ YES ballots. */
  private forkFactionSize(f: ForkVote): number {
    const set = new Set<string>();
    try {
      const arr = JSON.parse(f.faction) as unknown;
      if (Array.isArray(arr)) for (const h of arr) if (typeof h === 'string') set.add(h);
    } catch {
      /* malformed faction JSON — count ballots only */
    }
    for (const b of atlas.forkBallots.values()) {
      if (b.forkId === f.forkId && b.yes) set.add(b.voter);
    }
    return set.size;
  }

  // ───────────────────────────── render ─────────────────────────────

  private renderList(): void {
    if (!this.listEl) return;
    this.listEl.innerHTML = '';

    const me = atlas.identityHex;
    const visible = [...atlas.groups.values()].filter(
      (g) => g.state === 'in_review' || (me != null && this.myMembership(g.groupId) != null),
    );
    visible.sort((a, b) => {
      const sa = STATE_ORDER[a.state] ?? 9;
      const sb = STATE_ORDER[b.state] ?? 9;
      if (sa !== sb) return sa - sb;
      return a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0;
    });

    if (visible.length === 0) {
      this.listEl.appendChild(this.empty('No groups in review, and you have not joined one yet.'));
      return;
    }

    for (const g of visible) {
      const card = document.createElement('div');
      card.className = 'ev-item grp-item';
      card.dataset.groupId = g.groupId.toString();
      card.innerHTML = this.cardHtml(g.groupId);
      this.listEl.appendChild(card);
    }
  }

  /** Full inner HTML for one group card, branching on lifecycle state. */
  private cardHtml(groupId: bigint): string {
    const g = atlas.groups.get(groupId.toString());
    if (!g) return '';
    const members = this.membersOf(groupId);
    const mine = this.myMembership(groupId);
    const role = mine?.role ?? null;

    const badges: string[] = [
      `<span class="badge ${STATE_CLS[g.state] ?? ''}">${STATE_LABEL[g.state] ?? g.state}</span>`,
    ];
    if (role) badges.push(`<span class="badge">${esc(role)}</span>`);

    const charter = this.parseCharter(g.charterRef);
    const missionLine = charter && charter.mission
      ? `<div class="meta">\u201c${esc(charter.mission)}\u201d</div>`
      : '';

    let body = '';
    if (g.state === 'in_review') body = this.reviewBody(groupId);
    else if (g.state === 'active') body = this.activeBody(groupId, role);
    else body = `<div class="meta">This group is ${STATE_LABEL[g.state]?.toLowerCase() ?? g.state}.</div>`;

    return (
      `<div class="head"><span class="title">${esc(g.name)}</span><span>${badges.join(' ')}</span></div>` +
      missionLine +
      `<div class="meta">${members.length} member${members.length === 1 ? '' : 's'}` +
      (g.state === 'active' ? ` \u00b7 Treasury <span class="price">${g.treasurySov} SOV</span>` : '') +
      `</div>` +
      body
    );
  }

  private parseCharter(ref: string): CharterDoc | null {
    try {
      const doc = JSON.parse(ref) as Partial<CharterDoc>;
      return {
        mission: typeof doc.mission === 'string' ? doc.mission : '',
        rules: Array.isArray(doc.rules) ? doc.rules.filter((r: unknown): r is string => typeof r === 'string') : [],
        tags: Array.isArray(doc.tags) ? doc.tags.filter((t: unknown): t is string => typeof t === 'string') : [],
      };
    } catch {
      return null;
    }
  }

  /** Body for a group still in its public review window. */
  private reviewBody(groupId: bigint): string {
    const g = atlas.groups.get(groupId.toString());
    if (!g) return '';
    const sigs = this.signatureCount(groupId);
    const challenges = this.challengesOf(groupId);
    const signed = this.haveISigned(groupId);
    const trust = atlas.localReputation?.trustworthiness ?? null;
    const lowTrust = trust != null && trust < GROUP_MIN_TRUST_TO_JOIN;
    const reviewClosed = nowMicros() >= g.reviewUntil;

    let html = `<div class="meta">Signatures <b>${sigs}/${GROUP_CHARTER_SIGNATURES_REQUIRED}</b> \u00b7 review closes ${fmtWhen(g.reviewUntil)}${reviewClosed ? ' (closed)' : ''}</div>`;

    if (challenges.length > 0) {
      const items = challenges
        .map((c) => `<li>${esc(label(c.challenger))}: ${esc(c.reason)}</li>`)
        .join('');
      html += `<div class="meta" style="color:var(--ax-bad)">\u26a0 ${challenges.length} outstanding challenge${challenges.length === 1 ? '' : 's'} \u2014 activation is blocked:<ul>${items}</ul></div>`;
    }

    const actions: string[] = [];
    if (!signed) {
      const dis = lowTrust ? 'disabled' : '';
      const title = lowTrust
        ? `Needs Trustworthiness \u2265 ${GROUP_MIN_TRUST_TO_JOIN} (you have ${trust ?? '?'})`
        : 'Sign + join this charter';
      actions.push(
        `<button class="primary" data-act="sign" title="${title}" ${dis}>Sign charter</button>`,
      );
    } else {
      actions.push(`<span class="badge inst">you signed</span>`);
    }
    html +=
      `<div class="row" style="margin-top:6px"><input type="text" class="grp-challenge-reason" maxlength="160" placeholder="Reason to challenge\u2026" />` +
      `<button class="secondary" data-act="challenge">Challenge</button></div>` +
      `<div class="actions">${actions.join('')}</div>`;
    return html;
  }

  /** Body for an active group: treasury, spends, embezzlement, forks. */
  private activeBody(groupId: bigint, role: string | null): string {
    const g = atlas.groups.get(groupId.toString());
    if (!g) return '';
    const members = this.membersOf(groupId);
    const officer = role != null && isOfficer(role);

    // Donate.
    let html =
      `<div class="row" style="margin-top:6px"><input type="number" class="grp-donate-amt" min="1" step="1" placeholder="SOV to donate" />` +
      `<button class="primary" data-act="donate">Donate</button></div>`;

    // Propose spend (officers only).
    if (officer) {
      const cap = freeCap(g.treasurySov);
      html +=
        `<div class="sub" style="margin-top:8px">Officer spend \u2014 free up to <b>${cap} SOV</b>/day; more opens a member vote.</div>` +
        `<div class="row"><input type="number" class="grp-spend-amt" min="1" step="1" placeholder="SOV" />` +
        `<input type="text" class="grp-spend-purpose" maxlength="120" placeholder="Purpose" />` +
        `<button class="primary" data-act="propose-spend">Propose</button></div>`;
    }

    // Spend proposals + tallies.
    const spends = this.spendsOf(groupId);
    if (spends.length > 0) {
      html += `<div class="sub" style="margin-top:8px">Disbursements</div>`;
      for (const s of spends) html += this.spendHtml(s, members.length, role);
    }

    // Embezzlement report (any member, targets sitting officers other than self).
    const officers = members.filter((m) => isOfficer(m.role) && m.identity !== atlas.identityHex);
    if (officers.length > 0) {
      const opts = officers
        .map((m) => `<option value="${m.identity}">${esc(label(m.identity))} (${esc(m.role)})</option>`)
        .join('');
      html +=
        `<div class="sub" style="margin-top:8px">Report embezzlement \u2014 halves the officer's Trustworthiness and demotes them.</div>` +
        `<div class="row"><select class="grp-embezzle-target"><option value="">Select an officer\u2026</option>${opts}</select>` +
        `<button class="secondary" data-act="report-embezzle">Report</button></div>`;
    }

    // Fork / schism.
    html += this.forkSection(groupId, members, role);
    return html;
  }

  private spendHtml(s: TreasurySpend, memberCount: number, myRole: string | null): string {
    const st = spendStatus(s);
    const mine = s.officer === atlas.identityHex;
    let html =
      `<div class="ev-item" style="margin:6px 0">` +
      `<div class="head"><span class="title">${s.amountSov} SOV</span><span class="badge ${st.cls}">${st.text}</span></div>` +
      `<div class="meta">${esc(s.purpose || '(no purpose)')} \u00b7 by ${esc(label(s.officer))}</div>`;

    if (s.requiresVote && !s.approved && !s.executed) {
      const t = this.spendTally(s.spendId);
      const open = nowMicros() < s.voteClosesAt;
      html += `<div class="meta">${t.yes} yes / ${t.no} no \u2014 needs ${votePassNeeded(memberCount)} of ${memberCount} \u00b7 closes ${fmtWhen(s.voteClosesAt)}${open ? '' : ' (closed)'}</div>`;
      html +=
        `<div class="actions"><button class="primary" data-act="vote-spend-yes" data-spend-id="${s.spendId}">Vote yes</button>` +
        `<button class="secondary" data-act="vote-spend-no" data-spend-id="${s.spendId}">Vote no</button></div>`;
    }

    if (s.approved && !s.executed && mine && myRole != null && isOfficer(myRole)) {
      html += `<div class="actions"><button class="primary" data-act="execute-spend" data-spend-id="${s.spendId}">Execute</button></div>`;
    }

    html += `</div>`;
    return html;
  }

  private forkSection(groupId: bigint, members: GroupMember[], role: string | null): string {
    const g = atlas.groups.get(groupId.toString());
    if (!g) return '';
    const me = atlas.identityHex;
    const forks = this.forksOf(groupId);
    const openFork = forks.find((f) => !f.resolved) ?? null;
    let html = `<div class="sub" style="margin-top:8px">Fork / schism</div>`;

    if (!openFork && role != null) {
      // Faction picker — self is always part of the initiating faction.
      const others = members.filter((m) => m.identity !== me);
      const checks = others
        .map(
          (m) =>
            `<label class="grp-check"><input type="checkbox" class="grp-faction" value="${m.identity}" /> ${esc(label(m.identity))}</label>`,
        )
        .join('');
      html +=
        `<div class="meta">You (initiator) + anyone you select. Needs \u2265 ${forkInitNeeded(members.length)} of ${members.length} to initiate.</div>` +
        (checks ? `<div class="grp-checks">${checks}</div>` : '') +
        `<div class="actions"><button class="secondary" data-act="initiate-fork">Initiate fork</button></div>`;
    }

    for (const f of forks) {
      if (f.resolved) {
        html += `<div class="ev-item" style="margin:6px 0"><div class="meta">Resolved fork by ${esc(label(f.initiator))} \u2014 ${f.passed ? 'faction left with a proportional share' : 'did not reach quorum'}. Recorded to the Memory Book.</div></div>`;
        continue;
      }
      const size = this.forkFactionSize(f);
      const open = nowMicros() < f.closesAt;
      const share = proportionalShare(g.treasurySov, size, members.length);
      const takesHall = size >= forkHallNeeded(members.length);
      const amInitiator = f.initiator === me;
      let inner =
        `<div class="head"><span class="title">Schism by ${esc(label(f.initiator))}</span><span class="badge ritual">${size}/${members.length}</span></div>` +
        `<div class="meta">Closes ${fmtWhen(f.closesAt)}${open ? '' : ' (closed)'} \u00b7 share \u2248 ${share} SOV${takesHall ? ' \u00b7 would take the Hall' : ''}</div>`;
      const acts: string[] = [];
      if (!amInitiator && role != null) {
        acts.push(
          `<button class="primary" data-act="vote-fork-join" data-fork-id="${f.forkId}">Join schism</button>`,
          `<button class="secondary" data-act="vote-fork-decline" data-fork-id="${f.forkId}">Decline</button>`,
        );
      }
      if (amInitiator) {
        acts.push(
          `<button class="primary" data-act="resolve-fork" data-fork-id="${f.forkId}">Resolve fork</button>`,
        );
      }
      if (acts.length > 0) inner += `<div class="actions">${acts.join('')}</div>`;
      html += `<div class="ev-item" style="margin:6px 0">${inner}</div>`;
    }

    return html;
  }

  private empty(text: string): HTMLElement {
    const d = document.createElement('div');
    d.className = 'empty';
    d.textContent = text;
    return d;
  }

  // ───────────────────────────── actions ─────────────────────────────

  private onListClick(e: MouseEvent): void {
    const btn = (e.target as HTMLElement | null)?.closest('button[data-act]') as HTMLButtonElement | null;
    if (!btn || btn.disabled) return;
    const card = btn.closest('.grp-item') as HTMLElement | null;
    if (!card?.dataset.groupId) return;
    const groupId = BigInt(card.dataset.groupId);

    switch (btn.dataset.act) {
      case 'sign':
        this.sign(groupId);
        break;
      case 'challenge':
        this.challenge(groupId, card);
        break;
      case 'donate':
        this.donate(groupId, card);
        break;
      case 'propose-spend':
        this.proposeSpend(groupId, card);
        break;
      case 'vote-spend-yes':
        this.voteSpend(btn.dataset.spendId, true);
        break;
      case 'vote-spend-no':
        this.voteSpend(btn.dataset.spendId, false);
        break;
      case 'execute-spend':
        this.executeSpend(btn.dataset.spendId);
        break;
      case 'report-embezzle':
        this.reportEmbezzle(groupId, card);
        break;
      case 'initiate-fork':
        this.initiateFork(groupId, card);
        break;
      case 'vote-fork-join':
        this.voteFork(btn.dataset.forkId, true);
        break;
      case 'vote-fork-decline':
        this.voteFork(btn.dataset.forkId, false);
        break;
      case 'resolve-fork':
        this.resolveFork(btn.dataset.forkId);
        break;
      default:
        break;
    }
  }

  private sign(groupId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .signCharter({ groupId })
      .then(() => this.setNote('Charter signed.', 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private challenge(groupId: bigint, card: HTMLElement): void {
    const conn = atlas.conn;
    if (!conn) return;
    const input = card.querySelector('.grp-challenge-reason') as HTMLInputElement | null;
    const reason = (input?.value ?? '').trim();
    if (reason.length === 0) {
      this.setNote('Give a reason for the challenge.', 'err');
      return;
    }
    void conn.reducers
      .challengeCharter({ groupId, reason })
      .then(() => this.setNote('Challenge filed \u2014 activation is blocked.', 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private donate(groupId: bigint, card: HTMLElement): void {
    const conn = atlas.conn;
    if (!conn) return;
    const input = card.querySelector('.grp-donate-amt') as HTMLInputElement | null;
    const amountSov = parseAmount(input?.value ?? '');
    if (amountSov == null) {
      this.setNote('Enter a whole SOV amount to donate.', 'err');
      return;
    }
    void conn.reducers
      .donateTreasury({ groupId, amountSov })
      .then(() => this.setNote(`Donated ${amountSov} SOV to the Treasury.`, 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private proposeSpend(groupId: bigint, card: HTMLElement): void {
    const conn = atlas.conn;
    if (!conn) return;
    const amtIn = card.querySelector('.grp-spend-amt') as HTMLInputElement | null;
    const purposeIn = card.querySelector('.grp-spend-purpose') as HTMLInputElement | null;
    const amountSov = parseAmount(amtIn?.value ?? '');
    const purpose = (purposeIn?.value ?? '').trim();
    if (amountSov == null) {
      this.setNote('Enter a whole SOV amount to spend.', 'err');
      return;
    }
    if (purpose.length === 0) {
      this.setNote('Describe the purpose of the spend.', 'err');
      return;
    }
    void conn.reducers
      .proposeSpend({ groupId, amountSov, purpose })
      .then(() => this.setNote('Spend proposed.', 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private voteSpend(spendIdRaw: string | undefined, approve: boolean): void {
    const conn = atlas.conn;
    if (!conn || !spendIdRaw) return;
    const spendId = BigInt(spendIdRaw);
    void conn.reducers
      .voteSpend({ spendId, approve })
      .then(() => this.setNote(`Voted ${approve ? 'yes' : 'no'}.`, 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private executeSpend(spendIdRaw: string | undefined): void {
    const conn = atlas.conn;
    if (!conn || !spendIdRaw) return;
    const spendId = BigInt(spendIdRaw);
    void conn.reducers
      .executeSpend({ spendId })
      .then(() => this.setNote('Spend executed.', 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private reportEmbezzle(groupId: bigint, card: HTMLElement): void {
    const conn = atlas.conn;
    if (!conn) return;
    const sel = card.querySelector('.grp-embezzle-target') as HTMLSelectElement | null;
    const hex = sel?.value ?? '';
    if (!hex) {
      this.setNote('Select an officer to report.', 'err');
      return;
    }
    let officer: Identity;
    try {
      officer = Identity.fromString(hex);
    } catch {
      this.setNote('Invalid officer.', 'err');
      return;
    }
    void conn.reducers
      .recordEmbezzlement({ groupId, officer })
      .then(() => this.setNote('Embezzlement recorded \u2014 the officer was demoted.', 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private initiateFork(groupId: bigint, card: HTMLElement): void {
    const conn = atlas.conn;
    if (!conn) return;
    const me = atlas.identityHex;
    if (!me) return;
    const faction = new Set<string>([me]); // the initiator is always part of the faction
    card.querySelectorAll('.grp-faction:checked').forEach((el) => {
      const v = (el as HTMLInputElement).value;
      if (v) faction.add(v);
    });
    const list = [...faction];
    void conn.reducers
      .initiateFork({ groupId, faction: JSON.stringify(list) })
      .then(() => this.setNote(`Fork initiated with ${list.length} member(s).`, 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private voteFork(forkIdRaw: string | undefined, join: boolean): void {
    const conn = atlas.conn;
    if (!conn || !forkIdRaw) return;
    const forkId = BigInt(forkIdRaw);
    void conn.reducers
      .voteFork({ forkId, join })
      .then(() => this.setNote(join ? 'Joined the schism.' : 'Declined the schism.', 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private resolveFork(forkIdRaw: string | undefined): void {
    const conn = atlas.conn;
    if (!conn || !forkIdRaw) return;
    const forkId = BigInt(forkIdRaw);
    void conn.reducers
      .resolveFork({ forkId })
      .then(() => this.setNote('Fork resolved.', 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private setNote(text: string, cls: '' | 'ok' | 'err'): void {
    if (!this.foundNote) return;
    this.foundNote.className = cls ? `note ${cls}` : 'note';
    this.foundNote.textContent = text;
  }
}
